<template>
  <MainLayout
    :initialDrawerExpanded="false"
    contentClass="pa-md-4"
    :has-sidebar="false"
  >
    <template #header>
      <div class="overline">
        <PageBack />
      </div>
    </template>

    <template #content>
      <v-container fluid class="pa-0 pa-md-4">

        <!-- Header bar -->
        <div class="d-flex align-center mb-4">
          <v-icon class="mr-2">mdi-compare</v-icon>
          <h2>Test comparatif</h2>
          <v-btn text small @click="toggleHistory">
            <v-icon left small>mdi-history</v-icon>
            <span class="text-none">Historique</span>
          </v-btn>
          <v-btn v-if="turns.length" text small class="ml-2" @click="resetSession">
            <v-icon left small>mdi-refresh</v-icon>
            <span class="text-none">Nouveau</span>
          </v-btn>
        </div>

        <!-- Engine labels -->
        <v-card elevation="0" class="mb-4">
          <v-card-text class="py-2">
            <v-row v-if="engineAName || engineBName" align="center">
              <v-col cols="12" md="5" class="d-flex align-center">
                <v-icon small class="mr-1">mdi-alpha-a-circle-outline</v-icon>
                <span class="body-2 font-weight-medium">{{ engineAName || '—' }}</span>
              </v-col>
              <v-col cols="12" md="2" class="d-flex align-center justify-center">
                <v-icon color="grey">mdi-swap-horizontal</v-icon>
              </v-col>
              <v-col cols="12" md="5" class="d-flex align-center">
                <v-icon small class="mr-1">mdi-alpha-b-circle-outline</v-icon>
                <span class="body-2 font-weight-medium">{{ engineBName || '—' }}</span>
              </v-col>
            </v-row>
            <v-alert v-else-if="configLoaded" type="warning" dense text class="mb-0">
              Aucun moteur configuré. Configurez les moteurs A et B dans l'administration Django.
            </v-alert>
          </v-card-text>
        </v-card>

        <!-- Conversation thread -->
        <div v-if="turns.length" ref="thread" class="turn-thread mb-4">
          <div v-for="(turn, idx) in turns" :key="idx" class="mb-6">
            <!-- User message bubble -->
            <div class="d-flex justify-end mb-3">
              <div class="user-bubble body-2">{{ turn.userMessage }}</div>
            </div>

            <!-- Side-by-side responses -->
            <v-row>
              <v-col cols="12" md="6">
                <v-card elevation="0">
                  <v-card-title class="subtitle-2 font-weight-medium pb-1">
                    <v-icon left small>mdi-alpha-a-circle-outline</v-icon>
                    {{ engineAName || 'Moteur A' }}
                    <v-spacer />
                    <span v-if="turn.doneA" class="caption grey--text font-weight-regular">
                      {{ formatTokens(turn.promptTokensA, turn.completionTokensA) }}
                    </span>
                    <v-progress-circular
                      v-else-if="running && idx === turns.length - 1"
                      indeterminate size="16" width="2" class="ml-2"
                    />
                  </v-card-title>
                  <v-divider />
                  <v-card-text class="response-text markdown-body" style="min-height: 80px;">
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <span v-html="renderMarkdown(turn.responseA)" />
                    <span v-if="running && idx === turns.length - 1 && !turn.doneA" class="cursor">▌</span>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" md="6">
                <v-card elevation="0">
                  <v-card-title class="subtitle-2 font-weight-medium pb-1">
                    <v-icon left small>mdi-alpha-b-circle-outline</v-icon>
                    {{ engineBName || 'Moteur B' }}
                    <v-spacer />
                    <span v-if="turn.doneB" class="caption grey--text font-weight-regular">
                      {{ formatTokens(turn.promptTokensB, turn.completionTokensB) }}
                    </span>
                    <v-progress-circular
                      v-else-if="running && idx === turns.length - 1"
                      indeterminate size="16" width="2" class="ml-2"
                    />
                  </v-card-title>
                  <v-divider />
                  <v-card-text class="response-text markdown-body" style="min-height: 80px;">
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <span v-html="renderMarkdown(turn.responseB)" />
                    <span v-if="running && idx === turns.length - 1 && !turn.doneB" class="cursor">▌</span>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </div>
        </div>

        <!-- Input area -->
        <v-card elevation="0" class="mb-4">
          <v-card-text>
            <v-textarea
              v-model="currentMessage"
              :label="turns.length ? 'Répondre…' : 'Message'"
              outlined
              dense
              rows="3"
              auto-grow
              :disabled="running"
              hide-details
              @keydown.enter.ctrl.prevent="run"
              @keydown.enter.meta.prevent="run"
            />
            <div class="d-flex align-center mt-3">
              <v-btn
                color="primary"
                elevation="0"
                :loading="running"
                :disabled="!canRun"
                @click="run"
              >
                <v-icon left>mdi-play-outline</v-icon>
                <span class="text-none">Lancer</span>
              </v-btn>
              <v-btn
                v-if="turns.length && !running && !sessionSaved"
                class="ml-3"
                elevation="0"
                :loading="sessionSaving"
                @click="saveSession"
              >
                <v-icon left>mdi-content-save-outline</v-icon>
                <span class="text-none">Enregistrer</span>
              </v-btn>
              <v-chip v-if="sessionSaved" class="ml-3" small color="success" outlined>
                <v-icon left small>mdi-check</v-icon>
                Enregistré
              </v-chip>
              <span v-if="turns.length" class="caption grey--text ml-3">
                {{ turns.length }} tour{{ turns.length > 1 ? 's' : '' }}
              </span>
            </div>
          </v-card-text>
        </v-card>

        <!-- History panel -->
        <v-card v-if="showHistory" elevation="0" class="mt-4">
          <v-card-title class="subtitle-1 font-weight-medium pb-1">
            <v-icon left small>mdi-history</v-icon>
            Historique des comparaisons
            <v-spacer />
            <v-btn icon small :loading="loadingHistory" @click="loadHistory">
              <v-icon small>mdi-refresh</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-data-table
              :headers="historyHeaders"
              :items="history"
              :loading="loadingHistory"
              no-data-text="Aucune comparaison enregistrée."
              dense
              class="history-table"
              @click:row="loadRun"
            >
              <template v-slot:[`item.created_at`]="{ item }">
                {{ formatDate(item.created_at) }}
              </template>
              <template v-slot:[`item.message`]="{ item }">
                <span class="caption">{{ item.message.slice(0, 80) }}{{ item.message.length > 80 ? '…' : '' }}</span>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>

      </v-container>
    </template>
  </MainLayout>
</template>

<script>
import PageBack from '@/components/ui/PageBack.vue'
import MainLayout from '@/layout/MainLayout.vue'
import { fetchComparativeConfig, fetchComparativeRun, streamComparativeLeg, saveComparativeRun, fetchComparativeRuns } from '@/services/evalApi'
import MarkdownIt from 'markdown-it'

const md = new MarkdownIt({ breaks: true, linkify: true })

export default {
  name: 'AdminComparativeView',
  components: { PageBack, MainLayout },

  data() {
    return {
      engineAId: null,
      engineBId: null,
      engineAName: '',
      engineBName: '',
      configLoaded: false,

      turns: [],
      currentMessage: '',
      running: false,
      sessionSaved: false,
      sessionSaving: false,

      showHistory: false,
      loadingHistory: false,
      history: [],
      historyHeaders: [
        { text: 'Date', value: 'created_at', width: 160 },
        { text: 'Utilisateur', value: 'created_by_username', width: 120 },
        { text: 'Moteur A', value: 'engine_a_name', width: 160 },
        { text: 'Moteur B', value: 'engine_b_name', width: 160 },
        { text: 'Message', value: 'message' },
      ],
    }
  },

  computed: {
    canRun() {
      return this.engineAId && this.engineBId && this.currentMessage.trim() && !this.running
    },
  },

  async mounted() {
    try {
      const config = await fetchComparativeConfig()
      this.engineAId = config.engine_a_id || null
      this.engineBId = config.engine_b_id || null
      this.engineAName = config.engine_a_name || ''
      this.engineBName = config.engine_b_name || ''
    } catch {
      this.$store.commit('chat/SHOW_NOTIFICATION', { type: 'error', message: 'Erreur lors du chargement de la configuration.' })
    } finally {
      this.configLoaded = true
    }

    if (this.$route.params.runId) {
      try {
        const run = await fetchComparativeRun(this.$route.params.runId)
        this.loadRun(run)
      } catch {
        this.$store.commit('chat/SHOW_NOTIFICATION', { type: 'error', message: 'Comparaison introuvable.' })
      }
    }
  },

  watch: {
    showHistory(val) {
      if (val && !this.history.length) this.loadHistory()
    },
  },

  methods: {
    renderMarkdown(text) {
      return md.render(text || '')
    },

    formatTokens(prompt, completion) {
      if (prompt == null) return null
      return `${prompt + completion} (${prompt}+${completion})`
    },

    async run() {
      const userMessage = this.currentMessage.trim()
      const turnIdx = this.turns.length

      this.turns.push({
        userMessage,
        responseA: '',
        responseB: '',
        doneA: false,
        doneB: false,
        promptTokensA: null,
        completionTokensA: null,
        promptTokensB: null,
        completionTokensB: null,
      })

      this.currentMessage = ''
      this.running = true
      this.$nextTick(() => this.scrollToBottom())

      await Promise.all([
        this.streamLeg('A', turnIdx),
        this.streamLeg('B', turnIdx),
      ])

      this.running = false
      this.sessionSaved = false
    },

    buildMessages(side, turnIdx) {
      const msgs = []
      for (let i = 0; i < turnIdx; i++) {
        const t = this.turns[i]
        msgs.push({ role: 'user', content: t.userMessage })
        msgs.push({ role: 'assistant', content: side === 'A' ? t.responseA : t.responseB })
      }
      msgs.push({ role: 'user', content: this.turns[turnIdx].userMessage })
      return msgs
    },

    async streamLeg(side, turnIdx) {
      const messages = this.buildMessages(side, turnIdx)
      try {
        const response = await streamComparativeLeg(side.toLowerCase(), messages)
        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        for (;;) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop()

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            const raw = line.slice(6).trim()
            if (!raw || raw === 'end') continue
            try {
              const data = JSON.parse(raw)
              const turn = this.turns[turnIdx]
              if (data.text) {
                if (side === 'A') turn.responseA += data.text
                else turn.responseB += data.text
                this.$nextTick(() => this.scrollToBottom())
              }
              if (data.done) {
                if (side === 'A') {
                  turn.promptTokensA = data.prompt_tokens ?? null
                  turn.completionTokensA = data.completion_tokens ?? null
                  turn.doneA = true
                } else {
                  turn.promptTokensB = data.prompt_tokens ?? null
                  turn.completionTokensB = data.completion_tokens ?? null
                  turn.doneB = true
                }
              }
            } catch { /* skip malformed chunk */ }
          }
        }
      } catch (err) {
        const turn = this.turns[turnIdx]
        if (side === 'A') { turn.responseA = `[Erreur] ${err.message}`; turn.doneA = true }
        else { turn.responseB = `[Erreur] ${err.message}`; turn.doneB = true }
      }
    },

    async saveSession() {
      if (this.sessionSaved || !this.turns.length) return
      this.sessionSaving = true
      const last = this.turns[this.turns.length - 1]
      try {
        await saveComparativeRun({
          message: this.turns[0].userMessage,
          response_a: last.responseA,
          response_b: last.responseB,
          prompt_tokens_a: last.promptTokensA,
          completion_tokens_a: last.completionTokensA,
          prompt_tokens_b: last.promptTokensB,
          completion_tokens_b: last.completionTokensB,
          turns_json: this.turns,
        })
        this.sessionSaved = true
      } catch {
        this.$store.commit('chat/SHOW_NOTIFICATION', { type: 'error', message: 'Erreur lors de la sauvegarde.' })
      } finally {
        this.sessionSaving = false
      }
    },

    async resetSession() {
      if (this.turns.length && !this.sessionSaved) await this.saveSession()
      this.turns = []
      this.currentMessage = ''
      this.sessionSaved = false
      if (this.$route.params.runId) {
        this.$router.replace({ name: 'admin_comparative' })
      }
    },

    scrollToBottom() {
      const el = this.$refs.thread
      if (el) el.scrollIntoView({ block: 'end', behavior: 'smooth' })
    },

    async toggleHistory() {
      if (this.$route.params.runId) {
        await this.resetSession()
        this.showHistory = true
      } else {
        this.showHistory = !this.showHistory
      }
    },

    async loadHistory() {
      this.loadingHistory = true
      try {
        this.history = await fetchComparativeRuns()
      } catch {
        this.$store.commit('chat/SHOW_NOTIFICATION', { type: 'error', message: 'Erreur lors du chargement de l\'historique.' })
      } finally {
        this.loadingHistory = false
      }
    },

    loadRun(run) {
      if (run.turns_json && run.turns_json.length) {
        this.turns = run.turns_json.map(t => ({ ...t, doneA: true, doneB: true }))
      } else {
        this.turns = [{
          userMessage: run.message,
          responseA: run.response_a,
          responseB: run.response_b,
          promptTokensA: run.prompt_tokens_a,
          completionTokensA: run.completion_tokens_a,
          promptTokensB: run.prompt_tokens_b,
          completionTokensB: run.completion_tokens_b,
          doneA: true,
          doneB: true,
        }]
      }
      this.currentMessage = ''
      this.sessionSaved = true
      this.showHistory = false
      this.$router.replace({ name: 'admin_comparative', params: { runId: run.id } })
    },

    formatDate(iso) {
      return new Date(iso).toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit',
      })
    },
  },
}
</script>

<style scoped>
.user-bubble {
  background: var(--v-secondary-base);
  border-radius: 12px 12px 2px 12px;
  padding: 8px 14px;
  max-width: 75%;
  white-space: pre-wrap;
}

.response-text {
  font-family: inherit;
  font-size: 0.875rem;
  line-height: 1.6;
}
.markdown-body ::v-deep p { margin-bottom: 0.6em; }
.markdown-body ::v-deep p:last-child { margin-bottom: 0; }
.markdown-body ::v-deep ul,
.markdown-body ::v-deep ol { padding-left: 1.4em; margin-bottom: 0.6em; }
.markdown-body ::v-deep h1,
.markdown-body ::v-deep h2,
.markdown-body ::v-deep h3 { margin: 0.8em 0 0.4em; font-weight: 600; }
.markdown-body ::v-deep code {
  background: rgba(0,0,0,0.06);
  border-radius: 3px;
  padding: 0.1em 0.3em;
  font-size: 0.85em;
}
.markdown-body ::v-deep pre {
  background: rgba(0,0,0,0.06);
  border-radius: 4px;
  padding: 0.75em 1em;
  overflow-x: auto;
  margin-bottom: 0.6em;
}
.markdown-body ::v-deep pre code { background: none; padding: 0; }
.history-table ::v-deep tbody tr {
  cursor: pointer;
}
.cursor {
  animation: blink 1s step-end infinite;
}
@keyframes blink {
  50% { opacity: 0; }
}
</style>
